/**
 * ConfirmDialog - Reusable confirmation modal to replace window.confirm
 */
import React, { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "default";
  onConfirm: () => void;
  onCancel: () => void;
};

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "default",
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  // Escape closes; Tab cycles within the dialog; focus returns to the opener on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>("button");
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus?.();
    };
  }, [open, onCancel]);

  // Initial focus lands on the safe action.
  useEffect(() => {
    if (open) cancelBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const dialogStyle: React.CSSProperties = {
    background: "var(--color-surface)",
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: "90%",
    boxShadow: "var(--shadow-lg)",
    outline: "none",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    color: "var(--color-title)",
  };

  const messageStyle: React.CSSProperties = {
    fontSize: 14,
    color: "var(--color-muted)",
    marginBottom: 20,
    lineHeight: 1.5,
  };

  const buttonRowStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  };

  const btnBase: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
  };

  const cancelBtnStyle: React.CSSProperties = {
    ...btnBase,
    background: "var(--color-subsurface)",
    color: "var(--color-muted)",
  };

  const confirmBtnStyle: React.CSSProperties = {
    ...btnBase,
    background: variant === "danger" ? "var(--color-danger)" : "var(--color-brand)",
    color: "#fff",
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div
        ref={dialogRef}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        onClick={(e) => e.stopPropagation()}
      >
        <div id={titleId} style={titleStyle}>{title ?? t("common.confirm")}</div>
        <div id={messageId} style={messageStyle}>{message}</div>
        <div style={buttonRowStyle}>
          <button ref={cancelBtnRef} style={cancelBtnStyle} onClick={onCancel}>
            {cancelLabel ?? t("common.cancel")}
          </button>
          <button style={confirmBtnStyle} onClick={onConfirm}>
            {confirmLabel ?? t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
